import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import RealMap from "../Maps/RealMap";
import { Toaster } from "sonner";
import { ChevronDown, X, Maximize2 } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import SendNotificationModal from "../Driver/SendNotificationModal";
import { XCircle, CheckCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useGlobalContext } from "../../GlobalContext";

const CustomSelectDriver = ({
  agents,
  selectedAgentId,
  setSelectedAgentId,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleSelect = (agent) => {
    if (agent.dutyStatus === "offline") return;
    setSelectedAgentId(agent.driverId);
    setIsOpen(false);
  };

  const selectedAgent = agents.find((a) => a.driverId === selectedAgentId);

  return (
    <div className="relative w-full mb-4">
      <button
        type="button"
        className="w-full border px-3 py-2 rounded flex justify-between items-center bg-white"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span>
          {selectedAgent
            ? `${selectedAgent.fullName} (${selectedAgent.dutyStatus})`
            : "Select Driver"}
        </span>
        <ChevronDown className="w-4 h-4 text-gray-600" />
      </button>

      {isOpen && (
        <ul className="absolute z-10 mt-1 w-full border bg-white rounded shadow-lg max-h-60 overflow-y-auto">
          {agents.map((agent) => (
            <li
              key={agent.driverId}
              className={`flex justify-between items-center px-4 py-2 hover:bg-gray-100 ${
                agent.dutyStatus === "offline"
                  ? "opacity-50 cursor-not-allowed"
                  : "cursor-pointer"
              }`}
              onClick={() => handleSelect(agent)}
            >
              <span>{agent.fullName}</span>
              {agent.dutyStatus === "offline" && (
                <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">
                  Offline
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const taskTabs = ["all", "unassigned", "assigned", "completed", "orderdelay"];

// Shimmer component for task cards
const TaskCardShimmer = () => (
  <div className="bg-white rounded-lg shadow border-l-4 p-3 border-gray-300 animate-pulse">
    <div className="flex justify-between items-center mb-2">
      <div className="h-4 bg-gray-200 rounded w-2/3 animate-pulse" />
      <div className="h-6 w-20 bg-gray-200 rounded animate-pulse" />
    </div>
    <hr className="my-2 border-gray-200" />
    <div className="space-y-2 bg-gray-100 p-3 rounded text-xs">
      <div className="h-3 bg-gray-200 rounded w-5/6 animate-pulse" />
      <div className="h-3 bg-gray-200 rounded w-4/6 animate-pulse" />
      <div className="h-3 bg-gray-200 rounded w-3/6 animate-pulse" />
    </div>
    <div className="mt-2 h-4 w-16 bg-gray-200 rounded animate-pulse" />
  </div>
);

// Modal shimmer component
const ModalContentShimmer = () => (
  <div className="animate-pulse">
    <div className="flex flex-col lg:flex-row gap-4">
      {/* Map placeholder */}
      <div className="w-full lg:w-1/2 h-64 bg-gray-200 rounded animate-pulse" />

      {/* Table placeholder */}
      <div className="w-full lg:w-1/2">
        <div className="w-full border rounded">
          {Array.from({ length: 7 }).map((_, index) => (
            <div key={index} className="flex border-b">
              <div className="w-1/3 px-2 py-3 bg-gray-100">
                <div className="h-3 bg-gray-200 rounded animate-pulse" />
              </div>
              <div className="w-2/3 px-2 py-3">
                <div className="h-3 bg-gray-200 rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Address section */}
    <div className="mt-4 space-y-2">
      <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse" />
      <div className="h-4 bg-gray-200 rounded w-2/3 animate-pulse" />
    </div>

    {/* Items section */}
    <div className="mt-6">
      <div className="h-5 bg-gray-200 rounded w-1/4 mb-2 animate-pulse" />
      <div className="w-full border rounded">
        <div className="bg-gray-100 border-b">
          <div className="flex">
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className="flex-1 px-2 py-2">
                <div className="h-3 bg-gray-200 rounded animate-pulse" />
              </div>
            ))}
          </div>
        </div>
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="flex border-b">
            {Array.from({ length: 5 }).map((_, cellIndex) => (
              <div key={cellIndex} className="flex-1 px-2 py-2">
                <div className="h-3 bg-gray-200 rounded animate-pulse" />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  </div>
);

export default function MapDashboard({
  tasks,
  agents,
  loading,
  isLoading,
  fromDate,
  toDate,
  shops,
  selectedTeam,
  setSelectedTeam,
  setFromDate,
  setToDate,
  FetchTaskCall,
  FetchDriverCall,
  SyncWithAnlook,
  AssignTaskToDriver,
  handleToggle,
  isChecked,
  baseurl,
  token,
  setIsUnauthorised,
}) {
  const { accessToken } = useGlobalContext();
  const [selectedTaskTab, setSelectedTaskTab] = useState("unassigned");
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [taskToAssign, setTaskToAssign] = useState(null);
  const [selectedAgentId, setSelectedAgentId] = useState("");
  const [selectedTask, setSelectedTask] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [notificationModalOpen, setNotificationModalOpen] = useState(false);
  const [notificationAgent, setNotificationAgent] = useState(null);
  const [isMapZoomed, setIsMapZoomed] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("All Status");
  const [taskSearchQuery, setTaskSearchQuery] = useState("");
  const [agentSearchQuery, setAgentSearchQuery] = useState("");
  const [selectedShop1, setSelectedShop1] = useState("");
  const [selectedAgentTab, setSelectedAgentTab] = useState("all drivers");
  const [team, setTeam] = useState([]);
  const [selectedShop, setSelectedShop] = useState("");
  const [localNewStatus, setLocalNewStatus] = useState("");
  const [assignmentLogs, setAssignmentLogs] = useState([]);
  const [statusChangeLogs, setStatusChangeLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);

  // shimmer effects
  const [isStatusChanging, setIsStatusChanging] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);

  const statusList = [
    "All Status",
    "Unassigned",
    "Assigned",
    "Started",
    "Inprogress",
    "Successful",
    "Food is Ready",
    "Acknowledged",
    "Failed",
    "Declined",
    "Cancelled",
    "Picked Up",
    "Arrived",
  ];

  const handleStatusChange = (newStatus) => {
    if (newStatus !== selectedStatus) {
      setIsStatusChanging(true);
      setSelectedStatus(newStatus);

      setTimeout(() => {
        setIsStatusChanging(false);
      }, 800);
    }
  };

  const handleTaskDetailsClick = (task) => {
    setSelectedTask(task);
    setShowDetailsModal(true);
    setIsModalLoading(true);

    setTimeout(() => {
      setIsModalLoading(false);
    }, 1200);
  };

  const statusOptions = [
    "unassigned",
    "assigned",
    "started",
    "inprogress",
    "successful",
    "food is ready",
    "acknowledged",
    "failed",
    "declined",
    "cancelled",
    "picked up",
    "arrived",
  ];

  const TaskCardSkeleton = () => (
    <div className="bg-white rounded-lg shadow border-l-4 p-3 border-orange-300 animate-pulse">
      <div className="flex justify-between items-center mb-2">
        <div className="h-4 bg-gray-200 rounded w-2/3" />
        <div className="h-6 w-20 bg-gray-200 rounded" />
      </div>
      <hr className="my-2 border-gray-200" />
      <div className="space-y-2 bg-gray-100 p-3 rounded text-xs">
        <div className="h-3 bg-gray-200 rounded w-5/6" />
        <div className="h-3 bg-gray-200 rounded w-4/6" />
        <div className="h-3 bg-gray-200 rounded w-3/6" />
      </div>
      <div className="mt-2 h-4 w-16 bg-gray-200 rounded" />
    </div>
  );

  const iconRed = new L.Icon({
    iconUrl:
      "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  });

  const iconYellow = new L.Icon({
    iconUrl:
      "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-yellow.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  });

  const iconDriverActive = new L.Icon({
    iconUrl: "http://bumppy.in/packky/api/v1/uploads/images/driver_icon.png",
    iconSize: [30, 30],
    iconAnchor: [15, 30],
    popupAnchor: [0, -25],
  });

  const agentTabs = ["all drivers", "active", "offline"];

  const isDateInRange = useCallback((taskDate, fromDate, toDate) => {
    if (!taskDate) return true;
    if (!fromDate && !toDate) return true;

    const taskDateObj = new Date(taskDate);
    const fromDateObj = fromDate ? new Date(fromDate) : null;
    const toDateObj = toDate ? new Date(toDate) : null;

    if (fromDateObj && taskDateObj < fromDateObj) return false;
    if (toDateObj && taskDateObj > toDateObj) return false;

    return true;
  }, []);

  const getAgentTabCount = useCallback(
    (tab) => {
      let list = agents || [];
      let driverIdsToShow = [];

      // If team is selected
      if (selectedTeam !== "All Team") {
        const selectedTeamData = team.find((t) => t.team_name === selectedTeam);

        if (selectedTeamData) {
          if (selectedShop) {
            const hasShop = selectedTeamData.shopList?.some(
              (shop) => shop.shop_code === selectedShop
            );

            if (hasShop) {
              driverIdsToShow =
                selectedTeamData.driverlist?.map((d) => d.id) || [];
            } else {
              return 0;
            }
          } else {
            driverIdsToShow =
              selectedTeamData.driverlist?.map((d) => d.id) || [];
          }

          list = list.filter(
            (a) =>
              driverIdsToShow.includes(a?.id?.toString()) ||
              driverIdsToShow.includes(a?.driverId?.toString())
          );
        }
      } else if (selectedShop) {
        const driversInSelectedShop = [];

        team.forEach((t) => {
          const hasShop = t.shopList?.some(
            (shop) => shop.shop_code === selectedShop
          );

          if (hasShop) {
            t.driverlist?.forEach((driver) => {
              driversInSelectedShop.push(driver.id);
            });
          }
        });

        list = list.filter(
          (a) =>
            driversInSelectedShop.includes(a?.id?.toString()) ||
            driversInSelectedShop.includes(a?.driverId?.toString())
        );
      }

      if (tab === "total" || tab === "all drivers") return list.length;

      return list.filter((agent) => agent?.dutyStatus?.toLowerCase() === tab)
        .length;
    },
    [agents, selectedTeam, selectedShop, team]
  );

  const statusFilteredTasks = useMemo(() => {
    if (!tasks || tasks.length === 0) return [];

    let filtered = tasks;

    if (selectedStatus !== "All Status" && selectedStatus !== "All") {
      filtered = filtered.filter((task) => {
        const taskStatus = task?.status?.toLowerCase();
        const filterStatus = selectedStatus?.toLowerCase();

        if (filterStatus === "food is ready") {
          return taskStatus === "food is ready";
        }
        if (filterStatus === "picked up") {
          return taskStatus === "picked up";
        }

        return taskStatus === filterStatus;
      });
    }

    if (selectedShop1) {
      filtered = filtered.filter((task) => {
        const taskShopId = task?.shopId || task?.shop_id || task?.restaurant_id;
        const taskShopCode =
          task?.shopcode || task?.shop_code || task?.restaurant_code;
        const selectedShopStr = selectedShop1.toString();

        return (
          (taskShopId && taskShopId.toString() === selectedShopStr) ||
          (taskShopCode && taskShopCode.toString() === selectedShopStr)
        );
      });
    }

    if (fromDate || toDate) {
      filtered = filtered.filter((task) =>
        isDateInRange(
          task?.created_at || task?.createdAt || task?.date,
          fromDate,
          toDate
        )
      );
    }

    return filtered;
  }, [tasks, selectedStatus, selectedShop1, fromDate, toDate, isDateInRange]);

  const getTaskTabCount = useCallback(
    (tab) => {
      if (!statusFilteredTasks || statusFilteredTasks.length === 0) return 0;

      switch (tab) {
        case "all":
          return statusFilteredTasks.length;
        case "unassigned":
          return statusFilteredTasks.filter(
            (t) => t?.status?.toLowerCase() === "unassigned"
          ).length;
        case "completed":
          return statusFilteredTasks.filter(
            (t) => t?.status?.toLowerCase() === "successful"
          ).length;
        case "assigned":
          return statusFilteredTasks.filter((t) =>
            [
              "assigned",
              "started",
              "inprogress",
              "food is ready",
              "acknowledged",
              "failed",
              "declined",
              "cancelled",
              "picked up",
              "arrived",
            ].includes(t?.status?.toLowerCase())
          ).length;
        case "orderdelay":
          return statusFilteredTasks.filter(
            (t) => t?.status?.toLowerCase() === "delayed"
          ).length;
        default:
          return statusFilteredTasks.filter(
            (t) => t?.status?.toLowerCase() === tab
          ).length;
      }
    },
    [statusFilteredTasks]
  );

  const filteredTasks = useMemo(() => {
    let result = statusFilteredTasks;

    if (selectedTaskTab !== "all") {
      switch (selectedTaskTab) {
        case "unassigned":
          result = result.filter(
            (t) => t?.status?.toLowerCase() === "unassigned"
          );
          break;
        case "completed":
          result = result.filter(
            (t) => t?.status?.toLowerCase() === "successful"
          );
          break;
        case "assigned":
          result = result.filter((t) =>
            [
              "assigned",
              "started",
              "inprogress",
              "food is ready",
              "acknowledged",
              "failed",
              "declined",
              "cancelled",
              "picked up",
              "arrived",
            ].includes(t?.status?.toLowerCase())
          );
          break;
        case "orderdelay":
          result = result.filter((t) => t?.status?.toLowerCase() === "delayed");
          break;
        default:
          result = result.filter(
            (t) => t?.status?.toLowerCase() === selectedTaskTab
          );
      }
    }

    if (taskSearchQuery && taskSearchQuery.trim() !== "") {
      const query = taskSearchQuery.toLowerCase();
      result = result.filter((task) =>
        [
          task?.order_id,
          task?.pk_task_id,
          task?.merchant_order_id,
          task?.contact_no,
          task?.user_id,
          task?.task_description,
          task?.driver_name,
          task?.driver_mobile,
          task?.team_id,
        ]
          .filter(Boolean)
          .some((field) => field?.toString()?.toLowerCase()?.includes(query))
      );
    }

    return result;
  }, [statusFilteredTasks, selectedTaskTab, taskSearchQuery]);

  const filteredAgents = useMemo(() => {
    let list = agents || [];
    let driverIdsToShow = [];

    // If team is selected (not "All Team")
    if (selectedTeam !== "All Team") {
      const selectedTeamData = team.find((t) => t.team_name === selectedTeam);

      if (selectedTeamData) {
        // If shop is also selected within this team
        if (selectedShop) {
          const hasShop = selectedTeamData.shopList?.some(
            (shop) => shop.shop_code === selectedShop
          );

          if (hasShop) {
            // Show drivers from this team only
            driverIdsToShow =
              selectedTeamData.driverlist?.map((d) => d.id) || [];
          } else {
            // Selected shop doesn't belong to selected team
            return [];
          }
        } else {
          // No shop selected, show all drivers from this team
          driverIdsToShow = selectedTeamData.driverlist?.map((d) => d.id) || [];
        }

        list = list.filter(
          (a) =>
            driverIdsToShow.includes(a?.id?.toString()) ||
            driverIdsToShow.includes(a?.driverId?.toString())
        );
      }
    } else if (selectedShop) {
      // "All Team" selected but shop is selected
      const driversInSelectedShop = [];

      team.forEach((t) => {
        const hasShop = t.shopList?.some(
          (shop) => shop.shop_code === selectedShop
        );

        if (hasShop) {
          t.driverlist?.forEach((driver) => {
            driversInSelectedShop.push(driver.id);
          });
        }
      });

      list = list.filter(
        (a) =>
          driversInSelectedShop.includes(a?.id?.toString()) ||
          driversInSelectedShop.includes(a?.driverId?.toString())
      );
    }

    // Filter by duty status tab
    if (selectedAgentTab !== "all drivers") {
      list = list.filter(
        (a) => a?.dutyStatus?.toLowerCase() === selectedAgentTab.toLowerCase()
      );
    }

    // Search filter
    if (agentSearchQuery && agentSearchQuery.trim() !== "") {
      const query = agentSearchQuery.toLowerCase();
      list = list.filter(
        (a) =>
          a?.fullName?.toLowerCase()?.includes(query) ||
          a?.driver_name?.toLowerCase()?.includes(query) ||
          a?.teamName?.toLowerCase()?.includes(query) ||
          a?.mobile?.toLowerCase()?.includes(query) ||
          a?.email?.toLowerCase()?.includes(query)
      );
    }

    return list;
  }, [
    agents,
    selectedTeam,
    selectedAgentTab,
    selectedShop,
    agentSearchQuery,
    team,
  ]);

  const handleAssignTask = async () => {
    if (!selectedAgentId || !taskToAssign) {
      console.error("Missing agent ID or task to assign");
      return;
    }

    try {
      const success = await AssignTaskToDriver(
        taskToAssign.pk_task_id,
        selectedAgentId
      );
      if (success) {
        setShowAssignModal(false);
        setTaskToAssign(null);
        setSelectedAgentId("");
        await FetchTaskCall();
      }
    } catch (error) {
      console.error("Error assigning task:", error);
    }
  };

  const getShopName = useCallback(
    (shopCode) => {
      if (!shopCode || !shops || shops.length === 0) return null;

      const shop = shops.find(
        (shop) =>
          shop.code === shopCode ||
          shop.shop_code === shopCode ||
          shop.restaurant_code === shopCode
      );

      return shop ? shop.shop_name || shop.name : null;
    },
    [shops]
  );

  const FetchTeamList = async () => {
    try {
      const UserDetail = { userid: "9" };
      const response = await axios.post(
        `${baseurl}/api/Driver/v2.0/getDriverTeamList`,
        UserDetail,
        { headers: { Authorization: `Basic ${token}` } }
      );

      if (response.data.responseCode === "00") {
        // toast.success(response.data.responseMessage);
        // console.log("✅ Login success:", response.data.responseMessage);
        setTeam(response.data.data);
      } else {
        toast.error(response.data.responseMessage);
      }
    } catch (error) {
      toast.error("Error fetching Driver Teams");
    }
  };

  const handleChangeStatus = async () => {
    if (!localNewStatus || !selectedTask) {
      toast.error("Please select a valid status.");
      return;
    }

    setIsModalLoading(true);
    const payload = {
      taskId: selectedTask.pk_task_id.toString(),
      status: localNewStatus,
      driverId: selectedTask.driver_id?.toString() || "0",
    };

    try {
      const response = await axios.post(
        `${baseurl}/api/Task/v1.0/changeTaskStatus`,
        payload,
        { headers: { Authorization: `Basic ${token}` } }
      );

      if (response.data.responseCode === "00") {
        toast.success(response.data.responseMessage || "Status updated.");
        await FetchTaskCall();
        setShowDetailsModal(false);
      } else if (response.data.responseCode === "401") {
        setIsUnauthorised(true);
      } else {
        toast.error(
          response.data.responseMessage || "Failed to update status."
        );
      }
    } catch (err) {
      toast.error("Error changing task status.");
    } finally {
      setIsModalLoading(false);
    }
  };

  const fetchTaskLogs = async (taskId) => {
    const payload = {
      fromDate,
      toDate,
      taskId: taskId.toString(),
    };

    try {
      // Assignment Logs
      const assignmentRes = await axios.post(
        `${baseurl}/api/Task/v1.0/getTaskAssignmentLogs`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (assignmentRes.data.responseCode === "00") {
        setAssignmentLogs(assignmentRes.data.data || []);
      } else if (assignmentRes.data.responseCode === "401") {
        setIsUnauthorised(true);
      } else {
        setAssignmentLogs([]);
      }

      // Status Change Logs
      const statusChangeRes = await axios.post(
        `${baseurl}/api/Task/v1.0/getTaskStatusChangeLogs`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (statusChangeRes.data.responseCode === "00") {
        setStatusChangeLogs(statusChangeRes.data.data || []);
      } else {
        setStatusChangeLogs([]);
      }
    } catch (err) {
      console.error("Error fetching task logs:", err);
      toast.error("Error fetching logs");
      setAssignmentLogs([]);
      setStatusChangeLogs([]);
    }
  };

  const handleLogsToggle = () => {
    if (!showLogs && selectedTask) {
      fetchTaskLogs(selectedTask.pk_task_id);
    }
    setShowLogs(!showLogs);
  };

  useEffect(() => {
    setSelectedShop("");
    FetchTeamList();
  }, [selectedTeam]);

  useEffect(() => {
    if (selectedTask?.status) {
      setLocalNewStatus(selectedTask.status.toLowerCase());
    }
  }, [selectedTask]);

  useEffect(() => {
    if (contentRef.current) {
      if (showLogs) {
        setContentHeight(contentRef.current.scrollHeight);
      } else {
        setContentHeight(0);
      }
    }
  }, [showLogs, statusChangeLogs, assignmentLogs]);

  return (
    <div className="flex flex-row flex-1 w-full overflow-hidden gap-5">
      {/* Left Panel: Task Overview */}
      <div className="flex-1 bg-gray-50 p-2">
        <div className="text-black flex justify-between font-semibold text-2xl bg-gray z-10">
          Tasks
          <div
            role="tablist"
            aria-label="Switch Auto/Manual"
            className={`relative inline-grid grid-cols-2 w-[88px] rounded-full bg-gray-100 border border-gray-200 p-1 ${
              loading ? "opacity-60 pointer-events-none" : ""
            }`}
          >
            {/* Sliding pill (white like Task/Map) */}
            <span
              aria-hidden="true"
              className="absolute top-1 bottom-1 left-1 rounded-full bg-white shadow transition-transform duration-300 ease-in-out pointer-events-none"
              style={{
                width: "calc(50% - 0.25rem)",
                transform: isChecked ? "translateX(100%)" : "translateX(0%)",
              }}
            />

            {/* Manual (left) */}
            <button
              type="button"
              role="tab"
              aria-selected={!isChecked}
              className="relative z-10 flex items-center justify-center px-1 py-1 rounded-full"
              onClick={() => handleToggle(false)}
            >
              <XCircle
                className={`h-4 w-4 ${
                  !isChecked ? "text-red-500" : "text-gray-500"
                }`}
              />
            </button>

            {/* Auto (right) */}
            <button
              type="button"
              role="tab"
              aria-selected={isChecked}
              className="relative z-10 flex items-center justify-center px-1 py-1 rounded-full"
              onClick={() => handleToggle(true)}
            >
              <CheckCircle
                className={`h-4 w-4 ${
                  isChecked ? "text-green-600" : "text-gray-500"
                }`}
              />
            </button>
          </div>
        </div>

        <Toaster position="top-right" />

        {/* Sticky Filters */}
        <div className="sticky top-0 z-10 bg-gray-50">
          <div className="flex flex-col xl:flex-row gap-4 py-2 w-full">
            {/* Search box */}
            <input
              type="text"
              placeholder="Search..."
              value={taskSearchQuery}
              onChange={(e) => setTaskSearchQuery(e.target.value)}
              className="rounded py-2 text-base border px-3 bg-white w-full xl:flex-1 focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
            />

            {/* Restaurant selection */}
            <div className="relative w-full xl:flex-1">
              <select
                id="shop-select"
                value={selectedShop1}
                onChange={(e) => setSelectedShop1(e.target.value)}
                disabled={loading}
                className={`w-full px-3 py-2 border rounded text-base text-gray-500 focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 pr-10 appearance-none ${
                  loading ? "bg-gray-100 cursor-not-allowed" : "bg-white"
                }`}
              >
                <option value="">
                  {loading ? "Loading shops..." : "All Restaurants"}
                </option>
                {Array.isArray(shops) &&
                  shops.map((shop, index) => (
                    <option
                      key={shop.id || shop.shop_id || index}
                      value={shop.code || shop.shop_code}
                    >
                      {shop.shop_name ||
                        shop.name ||
                        `Shop ${shop.id || shop.shop_id}`}
                    </option>
                  ))}
              </select>
              <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                <svg
                  className="w-4 h-4 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>

            {/* From Date */}
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="bg-white border px-3 py-2 rounded text-base text-gray-500 w-full xl:flex-1 focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
            />

            {/* To Date */}
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="bg-white border px-3 py-2 rounded text-base text-gray-500 w-full xl:flex-1 focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          {/* Task tabs */}
          <div className="flex gap-2 lg:gap-2 py-2 items-center border bg-white shadow-sm rounded-md overflow-x-auto">
            {taskTabs.map((tab) => (
              <div
                key={tab}
                className={`cursor-pointer px-3 lg:px-3 py-2 ml-2 flex items-center gap-2 lg:gap-2 text-sm lg:text-base font-medium rounded-md transition-all duration-200 whitespace-nowrap ${
                  selectedTaskTab === tab
                    ? "bg-[#F97316] text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
                onClick={() => setSelectedTaskTab(tab)}
              >
                <span>
                  {tab === "all"
                    ? "All Orders"
                    : tab === "orderdelay"
                    ? "Order Delay"
                    : tab === "completed"
                    ? "Completed"
                    : tab.charAt(0).toUpperCase() + tab.slice(1)}
                </span>
                <span
                  className={`px-2 py-1 text-xs lg:text-sm font-bold rounded-md ${
                    selectedTaskTab === tab
                      ? "bg-[#fb9d5c] text-white"
                      : "bg-gray-300 text-gray-800"
                  }`}
                >
                  {getTaskTabCount(tab)}
                </span>
              </div>
            ))}

            <div className="relative flex-1 xl:w-50 ml-2 me-2">
              <select
                className="border px-3 py-2 rounded text-gray-500 text-base bg-white appearance-none w-full focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                value={selectedStatus}
                onChange={(e) => handleStatusChange(e.target.value)}
              >
                {statusList.map((status, idx) => (
                  <option key={idx} value={status}>
                    {status}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                <svg
                  className="w-4 h-4 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Task List */}
        <div
          className="overflow-y-auto mt-5 gap-5"
          style={{ maxHeight: "calc(100vh - 280px)" }}
        >
          {isLoading || isStatusChanging ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, index) => (
                <TaskCardShimmer key={index} />
              ))}
            </div>
          ) : filteredTasks.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3 gap-6">
              {filteredTasks.map((task) => {
                const getCardStyling = (status) => {
                  switch (status?.toLowerCase()) {
                    case "unassigned":
                      return {
                        borderColor: "#FF8C00",
                        statusBg: "bg-orange-500",
                      };
                    case "assigned":
                      return {
                        borderColor: "#3B82F6",
                        statusBg: "bg-blue-500",
                      };
                    case "started":
                      return {
                        borderColor: "#eab308",
                        statusBg: "bg-yellow-500",
                      };
                    case "inprogress":
                      return {
                        borderColor: "#6366F1",
                        statusBg: "bg-indigo-500",
                      };
                    case "food is ready":
                      return {
                        borderColor: "#A855F7",
                        statusBg: "bg-purple-500",
                      };
                    case "picked up":
                      return { borderColor: "#0ea5e9", statusBg: "bg-sky-500" };
                    case "completed":
                    case "delivered":
                    case "successful":
                    case "arrived":
                      return {
                        borderColor: "#10B981",
                        statusBg: "bg-green-500",
                      };
                    case "cancelled":
                    case "cancel":
                    case "failed":
                    case "declined":
                      return { borderColor: "#EF4444", statusBg: "bg-red-500" };
                    default:
                      return {
                        borderColor: "#6B7280",
                        statusBg: "bg-gray-500",
                      };
                  }
                };

                const { borderColor, statusBg } = getCardStyling(task?.status);

                return (
                  <div
                    key={task?.pk_task_id || task?.id}
                    className="relative rounded-lg bg-white shadow-md border-l-4 px-5 py-4 hover:shadow-lg transition-shadow duration-200"
                    style={{ borderColor }}
                  >
                    <div className="flex items-start justify-between mb-3 min-w-0">
                      <div className="flex-1 min-w-0 mr-2">
                        <span className="text-sm font-semibold text-gray-800 block truncate">
                          Order: {task?.order_id || "N/A"}
                        </span>
                        <span className="text-xs text-gray-600 block truncate">
                          {task?.pk_task_id} / {task?.merchant_order_id}
                        </span>
                      </div>

                      {task?.status?.toLowerCase() === "unassigned" ? (
                        <button
                          className="text-xs bg-orange-500 text-white px-2 py-1 rounded hover:bg-orange-600 transition-colors duration-200 flex-shrink-0"
                          onClick={() => {
                            setTaskToAssign(task);
                            setSelectedAgentId("");
                            setShowAssignModal(true);
                          }}
                        >
                          Assign
                        </button>
                      ) : (
                        <span
                          className={`inline-flex items-center justify-center text-xs font-medium rounded-full px-2 py-1 flex-shrink-0 ${statusBg} text-white`}
                        >
                          {(task?.status || "Unknown").charAt(0).toUpperCase() +
                            (task?.status || "unknown").slice(1)}
                        </span>
                      )}
                    </div>

                    <div className="bg-gray-100 rounded-md px-3 py-2 space-y-2 text-xs text-gray-700 min-w-0">
                      <div className="flex min-w-0">
                        <span className="font-semibold flex-shrink-0">
                          Pickup:
                        </span>
                        <span className="ml-1 truncate">
                          {task?.pickup_address || "N/A"}
                        </span>
                      </div>
                      <div className="flex min-w-0">
                        <span className="font-semibold flex-shrink-0">
                          Drop:
                        </span>
                        <span className="ml-1 truncate">
                          {task?.delivery_address || "N/A"}
                        </span>
                      </div>
                      {task?.driver_name && (
                        <div className="flex min-w-0">
                          <span className="font-semibold flex-shrink-0">
                            Driver:
                          </span>
                          <span className="ml-1 truncate">
                            {task.driver_name} / {task?.driver_mobile || "N/A"}
                          </span>
                        </div>
                      )}
                      {getShopName(task?.shopcode || task?.shop_code) && (
                        <div className="flex min-w-0">
                          <span className="font-semibold flex-shrink-0">
                            Restaurant:
                          </span>
                          <span className="ml-1 truncate">
                            {getShopName(task?.shopcode || task?.shop_code)}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between mt-4">
                      <button
                        onClick={() => SyncWithAnlook(task?.pk_task_id)}
                        className="text-orange-500 hover:text-orange-600 transition-colors duration-200"
                        title="Sync With Anlook"
                      >
                        ⟳
                      </button>
                      <button
                        className="text-sm text-blue-600 hover:text-blue-800 underline transition-colors duration-200"
                        onClick={() => handleTaskDetailsClick(task)}
                      >
                        Details
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              <p>No tasks found for the selected filters</p>
            </div>
          )}
        </div>
      </div>

      <div className="border-1 border-gray-300"></div>

      {/* Right Panel: Agent Overview */}
      <div className="w-full lg:w-[400px] xl:w-[500px] min-w-0 bg-gray-50 flex flex-col h-screen">
        <div className="justify-between items-center px-3 py-1 text-xl lg:text-2xl font-semibold">
          <p className="mb-1 flex items-center justify-between">
            <span>Agents</span>
            <button
              onClick={() => FetchDriverCall()}
              className="text-orange-500 flex-shrink-0"
            >
              ⟳
            </button>
          </p>

          <div className="flex flex-col lg:flex-row gap-4 py-2">
            <div className="relative flex-1 lg:flex-none">
              <select
                className="border px-2 py-2 rounded text-sm text-gray-500 bg-white w-full lg:w-60 pr-8 appearance-none focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                value={selectedTeam}
                onChange={(e) => setSelectedTeam(e.target.value)}
              >
                <option value="All Team">All Team</option>
                {(team || []).map((t) => (
                  <option key={t?.Id} value={t?.team_name}>
                    {t?.team_name}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                <svg
                  className="w-4 h-4 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>

            <div className="relative flex-1">
              <select
                id="shop-select-agents"
                value={selectedShop}
                onChange={(e) => setSelectedShop(e.target.value)}
                disabled={loading}
                className={`w-full px-3 py-2 border text-sm text-gray-500 rounded focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 pr-10 appearance-none ${
                  loading ? "bg-gray-100 cursor-not-allowed" : "bg-white"
                }`}
              >
                <option value="">All Restaurants</option>
                {selectedTeam === "All Team"
                  ? team.flatMap((t) =>
                      (t.shopList || []).map((shop) => (
                        <option key={shop.id} value={shop.shop_code}>
                          {shop.shop_name}
                        </option>
                      ))
                    )
                  : (
                      team.find((t) => t.team_name === selectedTeam)
                        ?.shopList || []
                    ).map((shop) => (
                      <option key={shop.id} value={shop.shop_code}>
                        {shop.shop_name}
                      </option>
                    ))}
              </select>
              <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                <svg
                  className="w-4 h-4 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-3 text-sm w-full">
            <input
              type="text"
              placeholder="Search..."
              value={agentSearchQuery}
              onChange={(e) => setAgentSearchQuery(e.target.value)}
              className="rounded py-2 text-base border px-2 bg-white flex-1 text-start focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
        </div>

        {/* Agent tabs */}
        <div className="flex gap-2 lg:gap-5 mt-2 text-center pb-1 mr-4 ml-3">
          {agentTabs.map((tab) => {
            const isActive = tab === selectedAgentTab;
            return (
              <div
                key={tab}
                onClick={() => setSelectedAgentTab(tab)}
                className={`flex justify-center items-center flex-1 px-1 py-2 rounded-md border cursor-pointer transition-all ${
                  isActive
                    ? "bg-blue-500 text-white"
                    : "bg-white text-gray-600 border-gray-300"
                }`}
              >
                <span className="text-xs lg:text-sm font-medium whitespace-nowrap">
                  {tab === "all drivers"
                    ? "All Drivers"
                    : tab.charAt(0).toUpperCase() + tab.slice(1)}
                </span>
                <span
                  className={`ml-2 px-2 py-1 rounded-md text-xs font-bold ${
                    isActive
                      ? "bg-blue-400 text-white"
                      : "bg-gray-100 text-gray-700"
                  }`}
                >
                  {getAgentTabCount(tab)}
                </span>
              </div>
            );
          })}
        </div>

        {/* Agent list */}
        <div className="flex-1 overflow-y-auto p-1 space-y-2 ml-2 mt-2 min-h-0 max-h-96">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {filteredAgents.map((agent) => (
              <div
                key={agent?.id}
                className="flex flex-col bg-white p-3 pl-4 shadow-sm rounded-md border-l-4 cursor-pointer hover:bg-gray-50 min-w-0"
                style={{
                  borderLeftColor:
                    agent?.dutyStatus === "active" ? "#22C55E" : "#3B82F6",
                }}
              >
                <div className="flex justify-between items-center min-w-0">
                  <div className="font-semibold text-sm text-gray-900 truncate flex-1 mr-2">
                    {agent?.fullName || "Unknown Driver"}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      className="text-orange-600 hover:text-orange-800"
                      onClick={() => {
                        setNotificationAgent(agent);
                        setNotificationModalOpen(true);
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15 17h5l-1.405-1.405C19.79 14.79 20 13.918 20 13V9a7.002 7.002 0 00-5-6.708V2a1 1 0 00-2 0v.292A7.002 7.002 0 006 9v4c0 .918.21 1.79.595 2.595L5 17h5m5 0v1a3 3 0 11-6 0v-1m6 0H9"
                        />
                      </svg>
                    </button>
                    <span
                      className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${
                        agent?.dutyStatus === "active"
                          ? "bg-green-100 text-green-700"
                          : agent?.dutyStatus === "offline"
                          ? "bg-blue-100 text-blue-700"
                          : "bg-yellow-100 text-yellow-700"
                      }`}
                    >
                      <span className="w-2 h-2 rounded-full bg-current" />
                      {(agent?.dutyStatus || "unknown")
                        .charAt(0)
                        .toUpperCase() +
                        (agent?.dutyStatus || "unknown").slice(1)}
                    </span>
                  </div>
                </div>
                {agent?.teamName && (
                  <div className="flex items-center gap-2 text-xs text-gray-500 mt-1 min-w-0">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 text-gray-400 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3 7h18M3 12h18M3 17h18"
                      />
                    </svg>
                    <span className="truncate">{agent.teamName}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
          {filteredAgents.length === 0 && (
            <div className="text-xs text-gray-400 text-center py-4">
              No agents found.
            </div>
          )}
        </div>

        {/* Map section */}

        <div className="h-48 lg:h-64 border-t relative mx-3 mt-2 min-h-0 flex-shrink-0">
          {!isMapZoomed && (
            <button
              onClick={() => setIsMapZoomed(true)}
              className="absolute bottom-6 right-3 bg-white text-gray-900 p-2 rounded-lg shadow-lg z-20 transition-colors"
              title="Expand Map"
            >
              <Maximize2 size={18} />
            </button>
          )}
          <div className="w-full h-full relative z-10">
            <RealMap Task={filteredTasks} drivers={filteredAgents} />
          </div>
        </div>

        {/* Full Screen Map */}
        {isMapZoomed && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex">
            <div className="w-full h-full bg-white relative">
              <div className="absolute inset-0 pointer-events-none z-50">
                <button
                  onClick={() => setIsMapZoomed(false)}
                  className="absolute top-4 right-4 bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg shadow-lg transition-colors pointer-events-auto"
                  title="Close Map"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="w-full h-full relative z-10">
                <RealMap Task={filteredTasks} drivers={filteredAgents} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showDetailsModal && selectedTask && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full p-4 relative text-sm max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4 pb-3 border-b sticky top-0 bg-white z-10">
              <h2 className="text-lg font-semibold">Delivery Task Details</h2>
              <div className="flex items-center gap-2 sticky top-0">
                <select
                  className="p-2 border rounded text-xs"
                  value={localNewStatus}
                  onChange={(e) => setLocalNewStatus(e.target.value)}
                >
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>
                      {status.toUpperCase()}
                    </option>
                  ))}
                </select>

                <button
                  className="bg-blue-600 text-white px-3 py-1.5 rounded text-xs shadow hover:bg-blue-700"
                  onClick={() =>
                    handleChangeStatus(localNewStatus, selectedTask)
                  }
                >
                  Change Status
                </button>

                <button
                  title="Close Task Details"
                  className="flex items-center justify-center text-gray-800 w-8 h-8 rounded-full shadow hover:bg-gray-400"
                  onClick={() => {
                    setShowDetailsModal(false);
                    setIsModalLoading(false);
                  }}
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {isModalLoading ? (
              <ModalContentShimmer />
            ) : (
              <>
                <div className="flex flex-col lg:flex-row gap-4">
                  {(selectedTask?.dropoff_latitude ||
                    selectedTask?.pickup_latitude) &&
                    (selectedTask?.dropoff_longitude ||
                      selectedTask?.pickup_longitude) && (
                      <div className="w-full lg:w-1/2 h-64 rounded overflow-hidden border">
                        <MapContainer
                          center={[
                            selectedTask?.dropoff_latitude ||
                              selectedTask?.pickup_latitude,
                            selectedTask?.dropoff_longitude ||
                              selectedTask?.pickup_longitude,
                          ]}
                          zoom={14}
                          scrollWheelZoom={false}
                          style={{ height: "100%", width: "100%" }}
                        >
                          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                          {selectedTask?.pickup_latitude &&
                            selectedTask?.pickup_longitude && (
                              <Marker
                                position={[
                                  selectedTask.pickup_latitude,
                                  selectedTask.pickup_longitude,
                                ]}
                                icon={iconRed}
                              >
                                <Popup>Pickup Location</Popup>
                              </Marker>
                            )}
                          {selectedTask?.dropoff_latitude &&
                            selectedTask?.dropoff_longitude && (
                              <Marker
                                position={[
                                  selectedTask.dropoff_latitude,
                                  selectedTask.dropoff_longitude,
                                ]}
                                icon={iconYellow}
                              >
                                <Popup>Delivery Location</Popup>
                              </Marker>
                            )}
                          {selectedTask?.driver_latitude &&
                            selectedTask?.driver_longitude && (
                              <Marker
                                position={[
                                  selectedTask.driver_latitude,
                                  selectedTask.driver_longitude,
                                ]}
                                icon={iconDriverActive}
                              >
                                <Popup>Driver Location</Popup>
                              </Marker>
                            )}
                        </MapContainer>
                      </div>
                    )}

                  <div className="w-full lg:w-1/2 overflow-x-auto">
                    <table className="w-full text-xs border">
                      <tbody>
                        <tr className="border-b">
                          <td className="font-semibold px-2 py-1 bg-gray-100">
                            Customer
                          </td>
                          <td className="px-2 py-1">
                            {selectedTask?.customer_name || "N/A"}
                          </td>
                        </tr>
                        <tr className="border-b">
                          <td className="font-semibold px-2 py-1 bg-gray-100">
                            Contact
                          </td>
                          <td className="px-2 py-1">
                            {selectedTask?.contact_number || "N/A"}
                          </td>
                        </tr>
                        <tr className="border-b">
                          <td className="font-semibold px-2 py-1 bg-gray-100">
                            Email
                          </td>
                          <td className="px-2 py-1">
                            {selectedTask?.email_address || "N/A"}
                          </td>
                        </tr>
                        <tr className="border-b">
                          <td className="font-semibold px-2 py-1 bg-gray-100">
                            Driver
                          </td>
                          <td className="px-2 py-1">
                            {selectedTask?.driver_name || "N/A"}
                          </td>
                        </tr>
                        <tr className="border-b">
                          <td className="font-semibold px-2 py-1 bg-gray-100">
                            Driver Mobile
                          </td>
                          <td className="px-2 py-1">
                            {selectedTask?.driver_mobile || "N/A"}
                          </td>
                        </tr>
                        <tr className="border-b">
                          <td className="font-semibold px-2 py-1 bg-gray-100">
                            Distance
                          </td>
                          <td className="px-2 py-1">
                            {selectedTask?.taskDistance || "N/A"}
                          </td>
                        </tr>
                        <tr>
                          <td className="font-semibold px-2 py-1 bg-gray-100">
                            ETA
                          </td>
                          <td className="px-2 py-1">
                            {selectedTask?.taskEstimatedTime || "N/A"}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="mt-4 text-xs">
                  <div className="mb-2">
                    <span className="font-semibold">Pickup Address:</span>{" "}
                    {selectedTask?.pickup_address || "N/A"}
                  </div>
                  <div>
                    <span className="font-semibold">Delivery Address:</span>{" "}
                    {selectedTask?.delivery_address || "N/A"}
                  </div>
                </div>

                <div className="mt-4">
                  {/* Accordion Header */}
                  <div
                    onClick={handleLogsToggle}
                    className="px-4 py-2 flex justify-between items-center cursor-pointer bg-gray-100 hover:bg-gray-200 transition-colors"
                  >
                    <h3 className="text-sm font-semibold">
                      Status & Assignment Logs
                    </h3>
                    <span
                      className={`transition-transform duration-300 text-gray-600 ${
                        showLogs ? "rotate-180" : ""
                      }`}
                      style={{ fontSize: "16px" }}
                    >
                      ▼
                    </span>
                  </div>

                  {/* Accordion Content */}
                  <div
                    ref={contentRef}
                    style={{
                      height: `${contentHeight}px`,
                      transition: "height 300ms ease",
                      overflow: "hidden",
                    }}
                  >
                    <div className="p-3 border-t space-y-6 text-sm">
                      {/* Status Change Logs */}
                      {statusChangeLogs.length > 0 && (
                        <div className="border rounded p-3">
                          <h3 className="text-xs font-semibold mb-3 text-gray-700">
                            Status Change Logs
                          </h3>
                          <div className="w-full overflow-x-auto px-4 pb-6">
                            <div className="flex items-start justify-center relative">
                              {statusChangeLogs.map((log, idx) => {
                                const statusColorMap = {
                                  unassigned: "bg-gray-400",
                                  assigned: "bg-yellow-500",
                                  "food is ready": "bg-purple-500",
                                  "picked up": "bg-orange-500",
                                  inprogress: "bg-indigo-500",
                                  successful: "bg-green-500",
                                  failed: "bg-red-500",
                                  declined: "bg-pink-500",
                                  cancelled: "bg-gray-500",
                                  started: "bg-blue-500",
                                  acknowledged: "bg-teal-500",
                                  arrived: "bg-lime-500",
                                };

                                const statusColor =
                                  statusColorMap[log.status?.toLowerCase()] ||
                                  "bg-blue-500";

                                return (
                                  <div
                                    key={idx}
                                    className="flex flex-col items-center relative z-10 mx-6"
                                  >
                                    <div
                                      className={`w-8 h-8 flex items-center justify-center rounded-full text-white text-xs font-bold ${statusColor}`}
                                    >
                                      {idx + 1}
                                    </div>
                                    <div className="text-[10px] mt-1 text-center font-medium">
                                      {log.status?.toUpperCase() || "N/A"}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Assignment Logs */}
                      {assignmentLogs.length > 0 && (
                        <div className="border rounded p-3">
                          <h3 className="text-xs font-semibold mb-2 text-gray-700">
                            Assignment Logs
                          </h3>
                          <div className="overflow-x-auto">
                            <table className="table-auto w-full text-xs border">
                              <thead className="bg-gray-100">
                                <tr>
                                  <th className="p-2 border">Assign Type</th>
                                  <th className="p-2 border">Driver ID</th>
                                  <th className="p-2 border">Driver Name</th>
                                  <th className="p-2 border">Task Status</th>
                                  <th className="p-2 border">Status</th>
                                  <th className="p-2 border">
                                    Assignment Date
                                  </th>
                                  <th className="p-2 border">Process Date</th>
                                </tr>
                              </thead>
                              <tbody>
                                {assignmentLogs.map((log, idx) => (
                                  <tr key={idx}>
                                    <td className="p-2 border">
                                      {log.assignType || "N/A"}
                                    </td>
                                    <td className="p-2 border">
                                      {log.driverId || "N/A"}
                                    </td>
                                    <td className="p-2 border">
                                      {log.driverName?.trim() || "N/A"}
                                    </td>
                                    <td className="p-2 border">
                                      {log.taskStatus || "N/A"}
                                    </td>
                                    <td className="p-2 border">
                                      {log.status || "N/A"}
                                    </td>
                                    <td className="p-2 border">
                                      {log.assignmentDate || "N/A"}
                                    </td>
                                    <td className="p-2 border">
                                      {log.processDate || "N/A"}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {statusChangeLogs.length === 0 &&
                        assignmentLogs.length === 0 && (
                          <div className="text-gray-500 text-center py-4">
                            No logs available
                          </div>
                        )}
                    </div>
                  </div>
                </div>

                {selectedTask?.delivery_details?.items?.length > 0 ? (
                  <>
                    <h3 className="text-sm font-semibold mt-1 mb-2">
                      Delivery Items
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="min-w-[700px] w-full text-xs border">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-2 py-2 text-left">Item</th>
                            <th className="px-2 py-2 text-left">Type</th>
                            <th className="px-2 py-2 text-left">Description</th>
                            <th className="px-2 py-2 text-left">W×H×L</th>
                            <th className="px-2 py-2 text-left">Weight</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedTask.delivery_details.items.map(
                            (item, index) => (
                              <tr key={index} className="border-t">
                                <td className="px-2 py-2">
                                  {item?.itemName || "N/A"}
                                </td>
                                <td className="px-2 py-2">
                                  {item?.itemType || "N/A"}
                                </td>
                                <td className="px-2 py-2">
                                  {item?.itemDescription || "N/A"}
                                </td>
                                <td className="px-2 py-2">
                                  {item?.width || 0}×{item?.height || 0}×
                                  {item?.length || 0}
                                </td>
                                <td className="px-2 py-2">
                                  {item?.weight || "N/A"}
                                </td>
                              </tr>
                            )
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <p className="text-xs text-center text-gray-500 mt-4">
                    No delivery item details available
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {showAssignModal && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center">
          <div className="bg-white p-6 rounded shadow-lg w-[90%] max-w-md">
            <h2 className="text-lg font-semibold mb-4">
              Assign Driver to{" "}
              <span className="text-orange-500">
                {taskToAssign?.order_id || taskToAssign?.title}
              </span>
            </h2>
            <CustomSelectDriver
              agents={agents || []}
              selectedAgentId={selectedAgentId}
              setSelectedAgentId={setSelectedAgentId}
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowAssignModal(false)}
                className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignTask}
                className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600"
              >
                Assign
              </button>
            </div>
          </div>
        </div>
      )}

      {notificationModalOpen && (
        <SendNotificationModal
          isOpen={notificationModalOpen}
          onClose={() => setNotificationModalOpen(false)}
          driver={notificationAgent}
          baseurl={baseurl}
          token={token}
          setIsUnauthorised={setIsUnauthorised}
        />
      )}
    </div>
  );
}
